import React, { useState } from 'react';
import { ReactComponent as CheckIcon } from 'images/check-icon.svg';

import Expander from 'components/Expander/Expander';
import MoreLink from 'components/MoreLink';
import { GoalAction } from 'components/FinancialCheckup/FinancialDashboard/GoalsSection/useGoal';
import { AI_COACH_DISCLAIMER } from 'components/Footer/Disclaimers/Disclaimers';
import { renderDescriptionWithLink } from 'utils/renderDescriptionWithLink';
import { RoutePath } from 'enums/Routes';
import { OFFER_AVAILABLE_FROM_DASHBOARD_STATE } from 'utils/navigationStates';

import styles from './RecommendedActions.module.scss';

interface RecommendedActionsProps {
  actions: GoalAction[];
}

const MAX_VISIBLE_ACTIONS = 3;

const ActionItem: React.FC<{ action: GoalAction }> = ({ action }) => (
  <div className={styles.actionItem}>
    <div className={styles.iconContainer}>
      <CheckIcon className={styles.checkIcon} />
    </div>
    <div className={styles.textContainer}>
      <div className={styles.actionTitle}>{action.title}</div>
      <div className={styles.actionDescription}>
        {renderDescriptionWithLink({
          description: action.description,
          keyword: 'Plannery',
          route: RoutePath.OfferAvailable,
          className: styles.planneryLink,
          state: OFFER_AVAILABLE_FROM_DASHBOARD_STATE.state,
        })}
      </div>
    </div>
  </div>
);

const RecommendedActions: React.FC<RecommendedActionsProps> = ({ actions }) => {
  const [showAllActions, setShowAllActions] = useState(false);

  if (actions.length === 0) return null;

  const visibleActions = showAllActions ? actions : actions.slice(0, MAX_VISIBLE_ACTIONS);
  const hasMoreActions = actions.length > MAX_VISIBLE_ACTIONS && !showAllActions;

  return (
    <div className={styles.recommendedActions}>
      <div className={styles.divider} />
      <Expander
        label={<span className={styles.actionsText}>Next step{actions.length > 1 ? 's' : ''}</span>}
        labelClassname={styles.actionsHeader}
        translateArrow={false}
        isOpenByDefault
        content={
          <div className={styles.actionsContent}>
            {visibleActions.map((action) => (
              <ActionItem key={action.title} action={action} />
            ))}
            {hasMoreActions && (
              <MoreLink
                text="View more"
                onClick={() => setShowAllActions(true)}
                showChevron={false}
                className={styles.viewMoreLink}
              />
            )}
            <div className={styles.separator}>
              <p className={styles.aiDisclaimer}>{AI_COACH_DISCLAIMER}</p>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default RecommendedActions;
